import { isPresent } from '@luminovo/commons';
import {
    PackagingRuntype,
    PriceType,
    SiteDTO,
    SiteRuntype,
    SolutionComparisonCriteria,
    SourcingTemplateDTO,
    SourcingTemplateInputDTO,
    SourcingTemplateInventoryPreference,
    SourcingTemplateLimit,
    SourcingTemplateOfferAgeLimit,
    SourcingTemplateSourcingPreferenceOptions,
    SupplierAndStockLocationDTO,
    SupplierAndStockLocationDTORuntype,
    SupplierPreference,
    SupplierPreferenceRuntype,
    TotalCostOfOwnershipRuleDTORuntype,
} from '@luminovo/http-client';
import { z } from 'zod';

export type SourcingTemplateTabValue =
    | 'general'
    | 'solution-preference'
    | 'solution-tags-preferences'
    | 'supplier-preferences'
    | 'inventory-preferences'
    | 'total-cost-of-ownership-rules';

export type SourcingTemplateFormValues = z.infer<typeof sourcingTemplateFormValueSchema>;
export const sourcingTemplateFormValueSchema = z.object({
    general: z.object({
        name: z.string().min(1, `Name is required`),
        description: z.string().nullable(),
        isDefaultForSourcing: z.boolean(),
    }),
    solutionPreference: z.object({
        leadTimePreference: z.object({
            type: z.nativeEnum(SourcingTemplateSourcingPreferenceOptions),
            target: z.string().optional(),
        }),
        solutionComparisonCriteria: z.nativeEnum(SolutionComparisonCriteria),
        excludedPriceTypes: z.object({
            listPrice: z.boolean(),
            contractPrice: z.boolean(),
            quotePrice: z.boolean(),
            customerNegotiatedPrice: z.boolean(),
            purchasePrice: z.boolean(),
            standardPrice: z.boolean(),
        }),
        inventoryPreference: z.nativeEnum(SourcingTemplateInventoryPreference),
        packagingPreferences: z.array(PackagingRuntype),
        preferDirectApiIntegrations: z.boolean(),
        excludeMismatchedPricePoints: z.boolean(),
        prioritizePreferredManufacturers: z.boolean(),
        preferCustomerNegotiatedPrices: z.boolean(),
        isTcoEnabled: z.boolean(),
    }),
    solutionTagsPreferences: z.object({
        offerAgeLimit: z.object({
            oldAfterUpdateDays: z.number().nullable(),
            outdatedAfterUpdateDays: z.number().nullable(),
            expiringBeforeValidityDate: z.number().nullable(),
            expiredAfterValidityDate: z.number().nullable(),
        }),
    }),
    supplierPreferences: z.array(
        SupplierAndStockLocationDTORuntype.extend({
            preference: SupplierPreferenceRuntype,
        }),
    ),
    inventoryPreferences: z.array(
        SiteRuntype.extend({
            preference: SupplierPreferenceRuntype,
        }),
    ),
    totalCostOfOwnershipRules: z.array(TotalCostOfOwnershipRuleDTORuntype),
});

function convertSolutionPreferences(
    formValues: SourcingTemplateFormValues,
): SourcingTemplateInputDTO['solution_preference'] {
    const getExcludedPriceTypes = () =>
        [
            formValues.solutionPreference.excludedPriceTypes.listPrice ? PriceType.ListPrice : undefined,
            formValues.solutionPreference.excludedPriceTypes.contractPrice ? PriceType.ContractPrice : undefined,
            formValues.solutionPreference.excludedPriceTypes.quotePrice ? PriceType.QuotePrice : undefined,
            formValues.solutionPreference.excludedPriceTypes.customerNegotiatedPrice
                ? PriceType.CustomerNegotiatedPrice
                : undefined,
            formValues.solutionPreference.excludedPriceTypes.purchasePrice ? PriceType.PurchasePrice : undefined,
            formValues.solutionPreference.excludedPriceTypes.standardPrice ? PriceType.StandardPrice : undefined,
        ].filter(isPresent);

    const getOfferAgeLimit = (): SourcingTemplateOfferAgeLimit => {
        const convertToLimitType = (days: number | null): SourcingTemplateLimit =>
            isPresent(days) ? { type: 'Days', number: days } : { type: 'Never' };

        return {
            old_after_update: convertToLimitType(formValues.solutionTagsPreferences.offerAgeLimit.oldAfterUpdateDays),
            outdated_after_update: convertToLimitType(
                formValues.solutionTagsPreferences.offerAgeLimit.outdatedAfterUpdateDays,
            ),
            expiring_before_validity_date: convertToLimitType(
                formValues.solutionTagsPreferences.offerAgeLimit.expiringBeforeValidityDate,
            ),
            expired_after_validity_date: convertToLimitType(
                formValues.solutionTagsPreferences.offerAgeLimit.expiredAfterValidityDate,
            ),
        };
    };

    return {
        lead_time_preferences: formValues.solutionPreference.leadTimePreference,
        solution_comparison_criteria: formValues.solutionPreference.solutionComparisonCriteria,
        excluded_price_types: getExcludedPriceTypes(),
        offer_age_limit: getOfferAgeLimit(),
        inventory_preference: formValues.solutionPreference.inventoryPreference,
        packaging_preferences: formValues.solutionPreference.packagingPreferences,
        prefer_direct_api_integrations: formValues.solutionPreference.preferDirectApiIntegrations,
        exclude_mismatched_price_points: formValues.solutionPreference.excludeMismatchedPricePoints,
        prioritize_preferred_manufacturers: formValues.solutionPreference.prioritizePreferredManufacturers,
        prefer_customer_negotiated_prices: formValues.solutionPreference.preferCustomerNegotiatedPrices,
        is_tco_enabled: formValues.solutionPreference.isTcoEnabled,
    };
}

function convertSupplierPreferences(
    formValues: SourcingTemplateFormValues,
): SourcingTemplateInputDTO['supplier_and_stock_location_preferences'] {
    return formValues.supplierPreferences
        .map((sp) => ({
            supplier_and_stock_location_id: sp.id,
            kind: sp.preference,
        }))
        .filter((s) => s.kind !== SupplierPreference.NotApproved);
}

function convertInventoryPreferences(
    formValues: SourcingTemplateFormValues,
): SourcingTemplateInputDTO['inventory_site_preferences'] {
    return formValues.inventoryPreferences
        .map((ip) => ({
            inventory_site_id: ip.id,
            kind: ip.preference,
        }))
        .filter((s) => s.kind !== SupplierPreference.NotApproved);
}

function convertTotalCostOfOwnershipRules(
    formValues: SourcingTemplateFormValues,
): SourcingTemplateInputDTO['total_cost_of_ownership_rules'] {
    return formValues.totalCostOfOwnershipRules.map((tcoRule) => ({
        name: tcoRule.name,
        description: tcoRule.description,
        category: tcoRule.category,
        action: tcoRule.action,
        conditions: tcoRule.conditions,
    }));
}

export function convertSourcingTemplateFormValuesToDTO(
    formValues: SourcingTemplateFormValues,
): SourcingTemplateInputDTO {
    return {
        name: formValues.general.name,
        description: formValues.general.description,
        is_default: formValues.general.isDefaultForSourcing,
        solution_preference: convertSolutionPreferences(formValues),
        supplier_and_stock_location_preferences: convertSupplierPreferences(formValues),
        inventory_site_preferences: convertInventoryPreferences(formValues),
        total_cost_of_ownership_rules: convertTotalCostOfOwnershipRules(formValues),
        origin: null,
    };
}

export function createDefaultSourcingTemplateFormValues({
    supplierAndStockLocations,
    inventorySites,
}: {
    supplierAndStockLocations: SupplierAndStockLocationDTO[];
    inventorySites: SiteDTO[];
}): SourcingTemplateFormValues {
    const getSupplierPreferences = (): SourcingTemplateFormValues['supplierPreferences'] => {
        return supplierAndStockLocations.map((sp) => ({
            ...sp,
            preference: SupplierPreference.NotApproved,
        }));
    };

    const getInventoryPreferences = (): SourcingTemplateFormValues['inventoryPreferences'] => {
        return inventorySites.map((ip) => ({
            ...ip,
            preference: SupplierPreference.NotApproved,
        }));
    };

    return {
        general: {
            name: '',
            description: '',
            isDefaultForSourcing: false,
        },
        solutionPreference: {
            leadTimePreference: {
                type: SourcingTemplateSourcingPreferenceOptions.Fastest,
                target: undefined,
            },
            solutionComparisonCriteria: SolutionComparisonCriteria.TotalPrice,
            excludedPriceTypes: {
                listPrice: false,
                contractPrice: false,
                quotePrice: false,
                customerNegotiatedPrice: false,
                purchasePrice: false,
                standardPrice: false,
            },

            inventoryPreference: SourcingTemplateInventoryPreference.Neutral,
            packagingPreferences: [],
            preferDirectApiIntegrations: false,
            excludeMismatchedPricePoints: false,
            prioritizePreferredManufacturers: false,
            preferCustomerNegotiatedPrices: false,
            isTcoEnabled: false,
        },
        solutionTagsPreferences: {
            offerAgeLimit: {
                oldAfterUpdateDays: null,
                outdatedAfterUpdateDays: null,
                expiringBeforeValidityDate: null,
                expiredAfterValidityDate: null,
            },
        },
        supplierPreferences: getSupplierPreferences(),
        inventoryPreferences: getInventoryPreferences(),
        totalCostOfOwnershipRules: [],
    };
}

export function convertSourcingTemplateDTOToFormValues({
    sourcingTemplate,
    supplierAndStockLocations,
    inventorySites,
}: {
    sourcingTemplate: SourcingTemplateDTO;
    supplierAndStockLocations: SupplierAndStockLocationDTO[];
    inventorySites: SiteDTO[];
}): SourcingTemplateFormValues {
    const getOfferAgeLimit = (): SourcingTemplateFormValues['solutionTagsPreferences']['offerAgeLimit'] => {
        const convertToLimitType = (value: SourcingTemplateLimit): number | null =>
            value.type === 'Days' && isPresent(value.number) ? value.number : null;

        return {
            oldAfterUpdateDays: convertToLimitType(
                sourcingTemplate.solution_preference.offer_age_limit.old_after_update,
            ),
            outdatedAfterUpdateDays: convertToLimitType(
                sourcingTemplate.solution_preference.offer_age_limit.outdated_after_update,
            ),
            expiringBeforeValidityDate: convertToLimitType(
                sourcingTemplate.solution_preference.offer_age_limit.expiring_before_validity_date,
            ),
            expiredAfterValidityDate: convertToLimitType(
                sourcingTemplate.solution_preference.offer_age_limit.expired_after_validity_date,
            ),
        };
    };

    const getSupplierPreferences = (): SourcingTemplateFormValues['supplierPreferences'] => {
        return supplierAndStockLocations
            .map((sp) => ({
                ...sp,
                preference:
                    sourcingTemplate.supplier_and_stock_location_preferences.find(
                        (spp) => spp.supplier_and_stock_location_id === sp.id,
                    )?.kind ?? SupplierPreference.NotApproved,
            }))
            .sort((a, b) => {
                const PREFERENCE_SORT_ORDER: Record<SupplierPreference, number> = {
                    [SupplierPreference.Preferred]: 0,
                    [SupplierPreference.Approved]: 1,
                    [SupplierPreference.NotApproved]: 2,
                    [SupplierPreference.Excluded]: 3,
                };
                return PREFERENCE_SORT_ORDER[a.preference] - PREFERENCE_SORT_ORDER[b.preference];
            });
    };

    const getInventoryPreferences = (): SourcingTemplateFormValues['inventoryPreferences'] => {
        return inventorySites
            .map((ip) => ({
                ...ip,
                preference:
                    sourcingTemplate.inventory_site_preferences.find((ipp) => ipp.inventory_site_id === ip.id)?.kind ??
                    SupplierPreference.NotApproved,
            }))
            .sort((a, b) => {
                const PREFERENCE_SORT_ORDER: Record<SupplierPreference, number> = {
                    [SupplierPreference.Preferred]: 0,
                    [SupplierPreference.Approved]: 1,
                    [SupplierPreference.NotApproved]: 2,
                    [SupplierPreference.Excluded]: 3,
                };
                return PREFERENCE_SORT_ORDER[a.preference] - PREFERENCE_SORT_ORDER[b.preference];
            });
    };

    return {
        general: {
            name: sourcingTemplate.name,
            description: sourcingTemplate.description,
            isDefaultForSourcing: sourcingTemplate.is_default,
        },
        solutionPreference: {
            leadTimePreference: {
                type: sourcingTemplate.solution_preference.lead_time_preferences.type,
                target: sourcingTemplate.solution_preference.lead_time_preferences.target,
            },
            solutionComparisonCriteria: sourcingTemplate.solution_preference.solution_comparison_criteria,
            excludedPriceTypes: {
                listPrice: sourcingTemplate.solution_preference.excluded_price_types.includes(PriceType.ListPrice),
                contractPrice: sourcingTemplate.solution_preference.excluded_price_types.includes(
                    PriceType.ContractPrice,
                ),
                quotePrice: sourcingTemplate.solution_preference.excluded_price_types.includes(PriceType.QuotePrice),
                customerNegotiatedPrice: sourcingTemplate.solution_preference.excluded_price_types.includes(
                    PriceType.CustomerNegotiatedPrice,
                ),
                purchasePrice: sourcingTemplate.solution_preference.excluded_price_types.includes(
                    PriceType.PurchasePrice,
                ),
                standardPrice: sourcingTemplate.solution_preference.excluded_price_types.includes(
                    PriceType.StandardPrice,
                ),
            },

            inventoryPreference: sourcingTemplate.solution_preference.inventory_preference,
            packagingPreferences: sourcingTemplate.solution_preference.packaging_preferences,
            preferDirectApiIntegrations: sourcingTemplate.solution_preference.prefer_direct_api_integrations,
            excludeMismatchedPricePoints: sourcingTemplate.solution_preference.exclude_mismatched_price_points,
            prioritizePreferredManufacturers: sourcingTemplate.solution_preference.prioritize_preferred_manufacturers,
            preferCustomerNegotiatedPrices: sourcingTemplate.solution_preference.prefer_customer_negotiated_prices,
            isTcoEnabled: sourcingTemplate.solution_preference.is_tco_enabled,
        },
        solutionTagsPreferences: {
            offerAgeLimit: getOfferAgeLimit(),
        },
        supplierPreferences: getSupplierPreferences(),
        inventoryPreferences: getInventoryPreferences(),
        totalCostOfOwnershipRules: sourcingTemplate.total_cost_of_ownership_rules,
    };
}
